import React, { useCallback, useEffect, useRef, useState } from "react";
import S from "./BlockAnchor.module.scss";

interface BlockAnchorProps {
    anchor?: string;
    children: React.ReactNode;
}

const COPIED_FEEDBACK_MS = 1500;
const HEADING_SELECTOR = "h1, h2, h3, h4, h5, h6";

interface ButtonPosition {
    top: number;
    left: number;
}

const writeToClipboard = async (text: string) => {
    if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        return;
    }

    // Fallback for non-secure contexts / older browsers
    const textArea = document.createElement("textarea");
    textArea.value = text;
    textArea.style.position = "fixed";
    textArea.style.top = "0";
    textArea.style.left = "0";
    textArea.style.opacity = "0";
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    try {
        document.execCommand("copy");
    } finally {
        document.body.removeChild(textArea);
    }
};

export const BlockAnchor = ({ anchor, children }: BlockAnchorProps) => {
    const wrapperRef = useRef<HTMLDivElement | null>(null);
    const hideTimerRef = useRef<number | null>(null);
    const [copied, setCopied] = useState(false);
    const [position, setPosition] = useState<ButtonPosition | null>(null);
    const [isHovering, setIsHovering] = useState(false);

    // Shared hover-show/hide helpers — used by both the heading-bound DOM
    // listeners (attached imperatively in the effect below) and the button's
    // own React onMouseEnter/Leave handlers, so moving the cursor from the
    // headline to the button does not cause it to disappear.
    const showButton = useCallback(() => {
        if (hideTimerRef.current !== null) {
            window.clearTimeout(hideTimerRef.current);
            hideTimerRef.current = null;
        }
        setIsHovering(true);
    }, []);

    const scheduleHide = useCallback(() => {
        if (hideTimerRef.current !== null) window.clearTimeout(hideTimerRef.current);
        hideTimerRef.current = window.setTimeout(() => {
            setIsHovering(false);
            hideTimerRef.current = null;
        }, 300);
    }, []);

    // Reset the "Copied!" state after a short delay.
    useEffect(() => {
        if (!copied) return;
        const timer = window.setTimeout(() => setCopied(false), COPIED_FEEDBACK_MS);
        return () => window.clearTimeout(timer);
    }, [copied]);

    // Combined effect:
    //  - measure the first heading's position so the copy button sits next to it
    //  - bind hover listeners to the heading (so only hovering the headline shows the icon)
    //  - perform the initial scroll-into-view exactly once, after the heading exists,
    //    if this block matches the current URL hash (SPA async render workaround)
    useEffect(() => {
        if (!anchor || typeof window === "undefined") return;
        const wrapper = wrapperRef.current;
        if (!wrapper) return;

        let raf = 0;
        let boundHeading: HTMLElement | null = null;
        let hasScrolled = false;

        // Read the current hash up front; this effect is the one-shot scroll trigger.
        let currentHash = "";
        try {
            currentHash = decodeURIComponent(window.location.hash.slice(1));
        } catch {
            currentHash = window.location.hash.slice(1);
        }

        const bindHeadingHover = (heading: HTMLElement | null) => {
            if (heading === boundHeading) return;
            if (boundHeading) {
                boundHeading.removeEventListener("mouseenter", showButton);
                boundHeading.removeEventListener("mouseleave", scheduleHide);
            }
            boundHeading = heading;
            if (heading) {
                heading.addEventListener("mouseenter", showButton);
                heading.addEventListener("mouseleave", scheduleHide);
            }
        };

        const update = () => {
            const heading = wrapper.querySelector(HEADING_SELECTOR) as HTMLElement | null;

            // Update hover bindings + position
            bindHeadingHover(heading);

            if (!heading || heading.offsetParent === null) {
                setPosition(null);
                return;
            }

            // Heading is present — we no longer need the MutationObserver to
            // watch for its arrival. ResizeObserver on the heading (added below)
            // is enough to keep the button position in sync with layout shifts.
            if (observedHeading !== heading) {
                if (observedHeading) ro.unobserve(observedHeading);
                ro.observe(heading);
                observedHeading = heading;
            }
            mo.disconnect();

            const wRect = wrapper.getBoundingClientRect();
            const hRect = heading.getBoundingClientRect();
            setPosition({
                top: hRect.top - wRect.top + hRect.height / 2,
                left: hRect.right - wRect.left + 8,
            });

            // One-shot scroll-into-view: only after the heading actually exists.
            if (!hasScrolled && currentHash === anchor) {
                hasScrolled = true;
                // Small delay so layout (images, fonts) has a chance to settle.
                window.setTimeout(() => {
                    wrapperRef.current?.scrollIntoView({
                        behavior: "smooth",
                        block: "start",
                    });
                }, 50);
            }
        };

        const schedule = () => {
            window.cancelAnimationFrame(raf);
            raf = window.requestAnimationFrame(update);
        };

        let observedHeading: HTMLElement | null = null;
        const ro = new ResizeObserver(schedule);
        ro.observe(wrapper);

        // MutationObserver is used only to detect when the heading first
        // appears (Suspense / async data render). Once found it is disconnected
        // by `update()` to avoid firing for every nested DOM change in rich
        // blocks (carousels, lazy images, accordions, etc.).
        const mo = new MutationObserver(schedule);
        mo.observe(wrapper, { childList: true, subtree: true });

        schedule();

        window.addEventListener("resize", schedule);

        // If the user navigates to a new anchor on the same page, re-arm the scroll.
        const onHashChange = () => {
            try {
                currentHash = decodeURIComponent(window.location.hash.slice(1));
            } catch {
                currentHash = window.location.hash.slice(1);
            }
            hasScrolled = false;
            // Re-attach the MO in case the heading has since been replaced.
            mo.observe(wrapper, { childList: true, subtree: true });
            schedule();
        };
        window.addEventListener("hashchange", onHashChange);

        return () => {
            window.cancelAnimationFrame(raf);
            if (hideTimerRef.current !== null) {
                window.clearTimeout(hideTimerRef.current);
                hideTimerRef.current = null;
            }
            ro.disconnect();
            mo.disconnect();
            window.removeEventListener("resize", schedule);
            window.removeEventListener("hashchange", onHashChange);
            bindHeadingHover(null);
        };
    }, [anchor, showButton, scheduleHide]);

    const handleCopy = useCallback(async () => {
        if (!anchor || typeof window === "undefined") return;
        const url = `${window.location.origin}${window.location.pathname}#${encodeURIComponent(
            anchor
        )}`;
        try {
            await writeToClipboard(url);
            setCopied(true);
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error("Failed to copy anchor link", error);
        }
    }, [anchor]);

    if (!anchor) {
        return <>{children}</>;
    }

    const visible = !!position && (isHovering || copied);

    return (
        <div ref={wrapperRef} id={anchor} className={S.wrapper}>
            {visible ? (
                <button
                    type="button"
                    className={S.copyButton}
                    style={{ top: position!.top, left: position!.left }}
                    onMouseEnter={showButton}
                    onMouseLeave={scheduleHide}
                    onFocus={showButton}
                    onBlur={scheduleHide}
                    onClick={handleCopy}
                    aria-label={`Copy link to section "${anchor}"`}
                    title="Copy link to this section"
                >
                    {copied ? (
                        <span className={S.copiedText}>Copied!</span>
                    ) : (
                        <svg
                            className={S.icon}
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            aria-hidden="true"
                            focusable="false"
                        >
                            <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                            <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                        </svg>
                    )}
                </button>
            ) : null}
            {children}
        </div>
    );
};

export default BlockAnchor;
